const BigNumber = require('bignumber.js');
const CartLine = require('@/lib/Cart/CartLine');
const CartProductStockLevels = require('@/libraries/DigiTickets/Cart/CartProductStockLevels');
const CheckoutResult = require('@/lib/Checkout/CheckoutResult');
const Customer = require('@/lib/Customers/Customer');
const ErrorBag = require('@/libraries/DigiTickets/Errors/ErrorBag');
const EventSessionPreferences = require('@/lib/Sessions/EventSessionPreferences');
const FieldGroup = require('@/lib/CustomFields/FieldGroup');
const FieldLevel = require('@/lib/CustomFields/FieldLevel');
const Order = require('@/lib/Orders/Order');
const Payment = require('@/lib/Payments/Payment');
const PaymentPatterns = require('@/lib/PaymentMethods/PaymentPatterns');
const Reservation = require('@/lib/Reservations/Reservation');
const SalesTotals = require('@/lib/Calculation/SalesTotals');
const SellState = require('@/lib/Cart/SellState');
const TransactionInProgress = require('@/lib/Payments/TransactionInProgress');
const { cloneShallow } = require('@/functions/clone');
const { container } = require('@/container');
const { getHashOfFieldValues } = require('@/lib/CustomFields/fieldFunctions');
const { md5Hash } = require('@/functions/hash');
const { populate } = require('@/functions/objects');
const {
    toBool,
    toDate,
    toInt,
    toIntArray,
    toNullableBool,
    toNullableInt,
    toNullableString,
} = require('@/functions/transform');

/**
 * @constructor
 *
 * @param {object} [data]
 *
 * WIP splitting up the Cart model (the data) from the CartService (the logic).
 * The properties are copied from CartService where it is something to do with the data in the cart rather than to do
 * with processing the cart. The methods are copied from CartService where it does something simple like getting or
 * setting something with no side effects.
 *
 * This should contain no dependencies so that it can be serialized/unserialized (in JSON or stored in IndexedDB).
 */
const Cart = function (data = {}) {
    /**
     * A hash of the current contents of the cart.
     *
     * @see this.getContentHash()
     *
     * @type {null}
     */
    this.contentHash = null;

    /**
     * A hash of the initial contents of the cart. If we are editing an order we can compare this with the current cart
     * to easily see what if anything has changed.
     *
     * @see this.getContentHash()
     *
     * @type {null}
     */
    this.contentHashInitial = null;

    /**
     * @type {Customer}
     */
    this.customer = new Customer();

    /**
     * @type {?string}
     */
    this.notes = null;

    /**
     * A human readable reference entered by the user for this order (e.g. "Table 123").
     * Gets shown on the kitchen screen, and printed on the receipt.
     *
     * @type {?string}
     */
    this.staffRef = null;

    /**
     * Number of payments that have been attempted for this cart.
     * Gets incremented each time a new card transaction is attempted. The number is used to build a
     * unique thirdPartyID for payments.
     *
     * @type {number}
     */
    this.paymentsAttempted = 0;

    /**
     * A number (but non-sequential) ID for this cart/order generated on the device, long before the order is sent
     * to the server and has an order ID / bookingRef generated. This is saved with the order on the server
     * and also forms part of the thirdPartyID for payments associated with this order.
     *
     * NOTE: thirdPartyID is not unique in the database hence the creation of thirdPartyRef
     *
     * This can be used as a unique key for stashed carts / held orders.
     *
     * @type {?string}
     */
    this.thirdPartyID = null;

    /**
     * A number (but non-sequential) ID for this cart/order generated on the device, long before the order is sent
     * to the server and has an order ID / bookingRef generated.
     * This is saved with the order on the server in a unique column
     * and also forms part of the thirdPartyID for payments associated with this order.
     *
     * This can be used as a unique key for stashed carts / held orders.
     *
     * @type {?string}
     */
    this.thirdPartyRef = null;

    /**
     * Remembers whether the operator wanted to ignore the fact this cart shared a ref with another stashed cart
     *
     * @type {boolean}
     */
    this.ignoreDuplicateRef = false;

    /**
     * @type {boolean}
     */
    this.ignoreMissingOrderEditRefundDetails = false;

    /**
     * A list of which sessionIDs were last selected per eventID.
     *
     * @type {EventSessionPreferences} Keys are eventIDs and values are Sessions.
     */
    this.eventSessionPreferences = new EventSessionPreferences();

    /**
     * If the current cart came from a stashed cart, and it had been sent to the kitchen,
     * this will be a KitchenOrder representation of the cart at the point at which it was un-stashed.
     *
     * We determine if an order has been sent to the kitchen by if it has any fulfillments created.
     *
     * This is used if the cart is re-held so we can print a kitchen receipt showing what has changed.
     * The kitchen screen also prints the changes but that is handled separately.
     *
     * @type {?KitchenOrder}
     */
    this.restoredKitchenOrder = null;

    /**
     * Order level field instances.
     *
     * @type {FieldGroup}
     */
    this.fields = new FieldGroup({ level: FieldLevel.ORDER });

    /**
     * @type {CartLine[]}
     */
    this.itemList = [];

    /**
     * @type {?Reservation}
     */
    this.reservation = null;

    /**
     * @type {ErrorBag}
     */
    this.errors = new ErrorBag();

    /**
     * @type {boolean}
     */
    this.balancePaid = false;

    /**
     * Payments that have been made.
     *
     * @type {Payment[]}
     */
    this.payments = [];

    /**
     * Gift Aid declaration - should Gift Aid be claimed for the order?
     *
     * @type {?boolean} Null if not answered yet. Boolean if answered.
     */
    this.giftAid = null;

    /**
     * Use the increased Gift Aid prices on applicable items?
     * This is possible without this.giftAid being true, if the customer wants to donate anyway without
     * doing Gift Aid.
     *
     * @type {?boolean} Null if not answered yet. Boolean if answered.
     */
    this.giftAidPrices = null;

    /**
     * Used if this order refers to a previous order (as is the case when editing an order or paying the balance).
     *
     * @type {?Order}
     */
    this.originalOrder = null;

    /**
     * When editing an order this is the difference in total cost between the original order and the current
     * cart contents.
     * In general it will be 0 if nothing has changed, positive if things are added, negative if thing are removed.
     * This should be used for display purposes only and not for calculations.
     *
     * @type {number}
     */
    this.originalOrderDifference = 0.00;

    /**
     * Is this Cart modifying an existing order?
     * Note: this is now true when taking a balance payment or when editing an order,
     * because the same flow is now used for both.
     *
     * @type {boolean}
     */
    this.isEditingOrder = false;

    /**
     * The offer(s) that the operator has selected for the entire cart from the Discounts modal.
     * The keys are offerIDs and the values are the number of times that offer can be applied (if applicable).
     *
     * @type {Object<number, number>}
     */
    this.selectedManualOfferIDs = {};

    /**
     * The codes for offer(s) that the operator has selected for the entire cart from the Discounts modal.
     * The keys are offerIDs and the values are the code entered to activate the offer.
     *
     * @type {Object<number, string>}
     */
    this.selectedCouponCodeOfferCodes = {};

    /**
     * Object containing the totals (financial, line count, people count, etc.) for the cart.
     * This is not added to the hydration map because it gets recalculated after restoring a cart.
     * This is populated by CartService.calculateCart().
     *
     * @type {SalesTotals}
     */
    this.totals = new SalesTotals();

    /**
     * Information about a card transaction that has been started for this cart and has not yet completed.
     * Payment methods should set this when they start a transaction, and clear it when the transaction is completed,
     * (successfully or otherwise).
     * The best way to set this is to call cartService.setTransactionInProgress / cartService.clearTransactionInProgress
     * It will be used to recover a transaction if the page is refreshed during the payment process.
     *
     * @type {?TransactionInProgress}
     */
    this.transactionInProgress = null;

    /**
     * An array of field instance keys that were shown the last time the custom fields modal was opened.
     * The purpose is so we can automatically display the fields again if the available fields have changed
     * since they were last shown.
     *
     * @type {?string[]}
     */
    this.lastPresentedCustomFieldKeys = null;

    /**
     * This holds some configuration for the Sell screen - can items be removed, what type of items can be added, etc.
     *
     * The only part of this currently implemented is where to redirect the user after
     * completing the sale (used for order editing).
     *
     * The existing SellState object is being reused here because it already contains the properties we would
     * want to use.
     *
     * @type {SellState}
     */
    this.sellState = new SellState();

    /**
     * @type {CartProductStockLevels}
     */
    this.cartProductStockLevels = new CartProductStockLevels();

    /**
     * Sum of the quantities in cart for each product.
     *
     * @type {{branchID: number, productID: number, quantity: number, qtyBeforeAdjustment: number, requiredQuantity:
     *     number}[]}
     */
    this.cartProductQuantities = [];

    /**
     * An unconfirmed order may be created as part of the payment process.
     * Note: The API doesn't return a whole order, just some basic info, so we only store the orderID here.
     */
    this.unconfirmedOrderID = null;

    /**
     * Some scenarios may require the full unconfirmed order to be loaded.
     *
     * @type {Order|null}
     */
    this.unconfirmedOrder = null;

    /**
     * If you start to take payment, create an unconfirmed order, then cancel the payment, the unconfirmed order
     * will be cancelled and this.unconfirmedOrderID will be set to null.
     * This array will contain any previous unconfirmed order IDs that have been cancelled.
     *
     * @type {number[]}
     */
    this.previousUnconfirmedOrderIDs = [];

    /**
     * When was the cart created?
     * This is used as part of the thirdPartyRef, so if you regenerate a ref for the same Cart you'll
     * get the same value.
     *
     * See: ThirdPartyRefGenerator.js
     *
     * @type {Date}
     */
    this.createdAt = new Date();

    /**
     * If an error occurs while saving this order, it will be stored here. This is so that it is saved along with
     * the cart in local storage.
     * Without this the OrderErrorModal could be shown, then you could refresh the page to dismiss it without
     * taking any action.
     * With this, when the SellCtrl loads it checks for this error and displays the OrderErrorModal again.
     *
     * This is not an Error object necessarily because we can't (easily) store/restore those to/from IndexedDB.
     * So consider it just an object that has a message property.
     *
     * @type {{message: string, name?: string, resolved: boolean, errorID: null}[]}
     */
    this.orderSaveErrors = [];

    /**
     * If errors occur while saving payments for this order, they will be stored here.
     *
     * @type {{message: string, name?: string, resolved: boolean, errorID: null}[]}
     */
    this.paymentSaveErrors = [];

    /**
     * Once the checkout flow has been started (pay now button pressed) this will hold the result.
     * It may be successful or unsuccessful.
     *
     * @type {?CheckoutResult}
     */
    this.checkoutResult = null;

    /**
     * In the case where we are printing a receipt for a cart that didn't make it to an order this will be
     * used to track if the receipt has already been printed, for auto printing a UI updating purposes.
     *
     * @type {?Date}
     */
    this.receiptPrintedAt = null;

    /**
     * A timestamp of when the cart was last modified.
     *
     * @type {number}
     */
    this.modified = Date.now();

    /**
     * Is this a group booking?
     *
     * @type {boolean}
     */
    this.groupBooking = false;

    /**
     * If this is a group booking, should a group reference be collected when adding tickets?
     *
     * @type {boolean}
     */
    this.assignGroupBookingReference = false;

    /**
     * This object gets calculated by CartToReservationStructureMapper after lines are in their final state before
     * being sent to the API.
     *
     * @type {Object.<string, string>}
     */
    this.bookingGroups = {};

    populate(this, data);
};

Cart.prototype = {
    /**
     * @param {CartLine} cartItem
     */
    addItem(cartItem) {
        this.itemList.push(cartItem);

        if (!cartItem.lineNumber) {
            cartItem.lineNumber = container.cartLineNumberFactory.next();
        }
    },

    /**
     * @returns {CartLine[]}
     */
    getItems() {
        return this.itemList;
    },

    /**
     * Return an array containing a clone of each item in the cart so changes can be made to those items without
     * affecting the cart.
     *
     * @returns {CartLine[]}
     */
    exportItems() {
        return this.itemList.map((item) => item.clone());
    },

    /**
     * Return a line from the cart by its ID.
     * This ID is generated by CartItem.getID. See there for what it is comprised of.
     *
     * @param {string} id
     *
     * @returns {?CartLine}
     */
    getLineByID(id) {
        return this.itemList.find((line) => line.getID() === id) || null;
    },

    /**
     * @param {number} itemID
     * @return {CartLine[]}
     */
    getLinesByItemID(itemID) {
        return this.itemList.filter((line) => line.getItemID() === itemID);
    },

    /**
     * Return a line from the cart by its line number.
     *
     * @param {number|string} lineNumber
     *
     * @returns {?CartLine}
     */
    getLineByNumber(lineNumber) {
        lineNumber = parseInt(lineNumber, 10);
        return this.itemList.find((line) => line.lineNumber === lineNumber) || null;
    },

    /**
     * @param {string} thirdPartyRef
     *
     * @returns {?CartLine}
     */
    getLineByThirdPartyRef(thirdPartyRef) {
        return this.itemList.find((line) => line.thirdPartyRef === thirdPartyRef) || null;
    },

    /**
     * Get the total cost of everything in the cart (after discounts).
     * This does not account for any payments that have been made, it's just the total of items.
     *
     * @return {number}
     */
    getTotal() {
        return this.totals.getLineTotal();
    },

    /**
     * Get the total cost of everything in the cart (after discounts) taking into account any payments made for the
     * original order, **but not any new payments made for this cart**.
     *
     * @return {number}
     */
    getTotalDue() {
        // If we are making a balance payment, use the balance payment amount as the total due.
        if (this.sellState.balancePaymentAmount) {
            return this.sellState.balancePaymentAmount;
        }

        // Start with the total price of all items in the current cart.
        let totalDue = new BigNumber(this.getTotal());

        // If we're editing an order, take off any payments that have already been made
        // in previous order.
        if (this.originalOrder) {
            totalDue = totalDue.minus(this.originalOrder.getTotalPaid());
        }

        return totalDue.toNumber();
    },

    /**
     * Returns the total number of items in the cart (sum of the qty per line)
     */
    getTotalQty() {
        return this.itemList.reduce((totalQty, line) => totalQty + line.getQuantity(), 0);
    },

    /**
     * Removes a line from the cart.
     *
     * @param {CartLine} line
     *
     * @return void
     */
    removeItem(line) {
        const index = this.itemList.indexOf(line);
        this.itemList.splice(index, 1);

        // Remove any lines that had this line as a parent.
        this.itemList
            .filter((l) => l.parentThirdPartyRef === line.thirdPartyRef)
            .forEach((childLine) => this.removeItem(childLine));
    },

    /**
     * Remove a payment from the cart.
     *
     * @param {Payment} payment
     */
    removePayment(payment) {
        const index = this.payments.indexOf(payment);

        if (index !== -1) {
            this.payments.splice(index, 1);
        }
    },

    removePaymentByTransactionRef(ref) {
        for (let i = 0; i < this.payments.length; i++) {
            if (this.payments[i].transactionRef === ref) {
                this.payments.splice(i, 1);
                return;
            }
        }
    },

    /**
     * Check if there are items in the cart which could have Gift Aid, or if this is an edit in the original order.
     *
     * @return {boolean}
     */
    containsGiftAidableItems() {
        return !!this.itemList.find((i) => i.isGiftAidable())
               || !!(
                   this.originalOrder
                   && this.originalOrder.giftAid
               );
    },

    /**
     * Check if the cart contains any items that should be displayed on the kitchen screen.
     * (Disables the send to kitchen button in the hold modal if not).
     *
     * @return {boolean}
     */
    containsKitchenItems() {
        return !!this.itemList.find((i) => i.getItem().showOnKitchenScreen && i.quantity > 0);
    },

    /**
     * @return {boolean}
     */
    containsMemberships() {
        return !!this.itemList.find((i) => i.isMembershipPlan());
    },

    /**
     * @return {boolean}
     */
    containsMembershipRenewal() {
        return !!this.itemList.find((i) => i.renewingMembership);
    },

    /**
     * Remove all memberships from the cart.
     *
     * @returns {null}
     */
    clearMemberships() {
        let memberships = this.itemList.filter((item) => item.isMembershipPlan());
        memberships.forEach((membership) => this.removeItem(membership));
        return null;
    },

    /**
     * @return {boolean}
     */
    isOrderAdjustmentBalancePayment() {
        return (
            this.isEditingOrder
            && !this.contentHashChanged()
            && (this.originalOrder && this.originalOrder.remainingBalance !== 0)
        );
    },

    /**
     * @return {boolean}
     */
    isOrderAdjustmentRefund() {
        return this.isEditingOrder && this.originalOrderDifference <= 0 && this.getTotalQty() === 0;
    },

    /**
     * Does this order require a reservation?
     *
     * @return {boolean}
     */
    requiresReservation() {
        if (this.isOrderAdjustmentBalancePayment()) {
            return false;
        }

        if (this.isOrderAdjustmentRefund()) {
            return false;
        }

        return true;
    },

    /**
     * @returns {string|null}
     */
    getCurrentPaymentPattern() {
        // Items being added to the cart must either match the payment period of items already
        // in the cart (if any have a payment period), or be free
        // Checking the price is greater than 0 has the side effect of not counting refunds, which is good.
        for (let i = 0; i < this.itemList.length; i++) {
            if (this.itemList[i].isPositiveValue() && this.itemList[i].getItem().paymentPattern) {
                return this.itemList[i].getItem().paymentPattern;
            }
        }

        return null;
    },

    /**
     * @returns {boolean}
     */
    containsRepeatingPayments() {
        const currentPaymentPattern = this.getCurrentPaymentPattern();
        // There is a payment pattern for the items in the cart, and it is not a single payment.
        // So it must be a repeating payment.
        return currentPaymentPattern && currentPaymentPattern !== PaymentPatterns.SINGLE;
    },

    /**
     * @returns {?number}
     */
    getOriginalOrderID() {
        return this.originalOrder ? this.originalOrder.ID : null;
    },

    /**
     * @returns {?number}
     */
    getOriginalOrderTotal() {
        return this.originalOrder ? this.originalOrder.total : null;
    },

    getHydrationMap() {
        return {
            assignGroupBookingReference: toBool,
            balancePaid: toBool,
            cartProductStockLevels: {
                model: CartProductStockLevels,
            },
            checkoutResult: {
                model: CheckoutResult,
            },
            contentHash: toNullableString,
            contentHashInitial: toNullableString,
            createdAt: toDate,
            customer: {
                model: Customer,
            },
            errors: {
                model: ErrorBag,
            },
            eventSessionPreferences: {
                model: EventSessionPreferences,
            },
            fieldData: {},
            fields: {
                model: FieldGroup,
            },
            giftAid: toNullableBool,
            giftAidPrices: toNullableBool,
            groupBooking: toBool,
            ignoreDuplicateRef: toBool,
            isEditingOrder: toBool,
            itemList: {
                modelCollection: CartLine,
            },
            lastPresentedCustomFieldKeys: {},
            modified: toInt,
            notes: toNullableString,
            orderSaveErrors: {},
            originalOrder: {
                model: Order,
            },
            payments: {
                modelCollection: Payment,
            },
            paymentsAttempted: toInt,
            paymentSaveErrors: {},
            previousUnconfirmedOrderIDs: toIntArray,
            receiptPrintedAt: toDate,
            reservation: {
                model: Reservation,
            },
            selectedCouponCodeOfferCodes: {},
            selectedManualOfferIDs: {},
            sellState: {
                model: SellState,
            },
            staffRef: toNullableString,
            totals: {
                model: SalesTotals,
            },
            thirdPartyID: toNullableString,
            thirdPartyRef: toNullableString,
            transactionInProgress: {
                model: TransactionInProgress,
            },
            unconfirmedOrder: {
                model: Order,
            },
            unconfirmedOrderID: toNullableInt,
        };
    },

    /**
     * @return {Cart}
     */
    toSerializable() {
        // This used to do a cloneDeep of the Cart, but it has lots of properties so it takes too long on low-spec
        // hardware. Instead, just do a cloneShallow of the cart so we can modify its properties below,
        // and a shallow clone of anything within the cart we are going to modify below.
        /** @type {Cart} clone */
        let clone = cloneShallow(this);

        if (clone.originalOrder) {
            clone.originalOrder = cloneShallow(clone.originalOrder);
            // Trying to store the order.fields property (which is a FieldGroupCollection instance) in IndexedDB
            // causes an error (OE-201). We can discard it because order.fields is built from a combination of
            // order.orderLevelFieldGroup and item level field groups on its instances.
            delete clone.originalOrder.fields;
        }

        // Remove circular references from memberships.
        if (clone.customer) {
            clone.customer = cloneShallow(clone.customer);

            if (clone.customer.membership) {
                // Remove nested/unnecessary models from the customer's membership
                // to prevent circular references.
                clone.customer.membership = cloneShallow(clone.customer.membership);
                clone.customer.membership.makeLean();
            }

            if (clone.customer.account) {
                clone.customer.account = cloneShallow(clone.customer.account);
                clone.customer.account.memberships = [];
                clone.customer.account.firstActiveMembership = null;
            }
        }

        // Remove circular references from cart items.
        clone.itemList = clone.itemList.map(
            /**
             * @param {CartLine} i
             */
            (i) => {
                let itemClone = i.clone();
                itemClone.cart = null;

                // Don't save the child lines - they will be rebuilt as needed.
                // They also have a circular reference to the cart.
                itemClone.childLines = [];

                if (itemClone.item && itemClone.item.category) {
                    itemClone.item.category.items = null;
                }

                return itemClone;
            }
        );

        return clone;
    },

    addOrderSaveError(error) {
        this.orderSaveErrors.push({
            message: error.message,
            name: error.name,
            resolved: false,
            errorID: error.errorID || undefined,
        });
    },

    getUnresolvedOrderSaveErrors() {
        return this.orderSaveErrors.filter((error) => !error.resolved);
    },

    hasUnresolvedOrderSaveErrors() {
        return this.getUnresolvedOrderSaveErrors().length > 0;
    },

    addPaymentSaveError(error) {
        this.paymentSaveErrors.push({
            message: error.message,
            name: error.name,
            resolved: false,
            errorID: error.errorID || undefined,
        });
    },

    getUnresolvedPaymentSaveErrors() {
        return this.paymentSaveErrors.filter((error) => !error.resolved);
    },

    hasUnresolvedPaymentSaveErrors() {
        return this.getUnresolvedPaymentSaveErrors().length > 0;
    },

    /**
     * @return {FieldGroup}
     */
    getOrderLevelFieldGroup() {
        return this.fields;
    },

    /**
     * For compatibility with Order entity.
     *
     * @return {CartLine[]}
     */
    getLines() {
        return this.itemList;
    },

    /**
     * Produce a hash of the following properties of the cart:
     * - Items
     * - Sessions
     * - Line Quantities
     * - Line Prices
     *
     * Add anything else that changes what is considered "what items are being purchased and at what price".
     * This is currently used for calculating offers. So add anything else that could change the outcome
     * of offer calculation.
     */
    getContentHash() {
        return md5Hash(JSON.stringify(this.getContentObject()));
    },

    getContentObject() {
        // IMPORTANT: remember to update the tests in CartServiceTest.js when this structure changes because
        // the hardCoded contentHashes will no longer be correct.
        let result = {
            lines: [],
            membershipID: (this.customer && this.customer.membership) ? this.customer.membership.ID : null,
            selectedManualOfferIDs: this.selectedManualOfferIDs,
            selectedCouponCodeOfferCodes: this.selectedCouponCodeOfferCodes,
            fields: this.fields.instances.length > 0 ? getHashOfFieldValues(this.fields.instances) : null,
            groupBooking: this.groupBooking,
            // TODO: it would be great to be able to add new order notes (but the api won't allow this at the moment)
            // notes: md5Hash(this.notes),
        };
        for (let i = 0; i < this.itemList.length; i++) {
            const line = this.itemList[i].getContentObject();
            result.lines.push(line);
        }

        return result;
    },

    regenerateContentHash() {
        this.contentHash = this.getContentHash();
    },

    /**
     * Has the cart or any of the lines changed? Particularly useful when editing an order.
     *
     * @returns {boolean}
     */
    contentHashChanged() {
        // FIXME:
        //  This should regenerate the content hash / use the getContentHash() method
        //  instead of using the `this.contentHash` property to ensure it is up to date.
        //  However, this method is called from a template so that would lead to constant calculation.
        //  Remove the use in the cart.html template then make the fix.
        return this.contentHashInitial !== this.contentHash;
    },

    /**
     * Returns true if this cart contains any items from a branch other than the supplied branchID.
     *
     * @param {number} orderBranchID
     *
     * @return {boolean}
     */
    isCrossBranch(orderBranchID) {
        for (let i = 0; i < this.itemList.length; i++) {
            if (this.itemList[i].branch.ID !== orderBranchID) {
                return true;
            }
        }

        return false;
    },

    /**
     * @param {CartLine} line
     *
     * @return {{branchID: number, productID: number, quantity: number, qtyBeforeAdjustment: number, requiredQuantity:
     *     number}|null}
     */
    getCartProductQuantityForLine(line) {
        return this.cartProductQuantities.find(
            (qty) => qty.branchID === line.branch.ID && qty.productID === line.item.ID
        ) || null;
    },

    /**
     * @param {CartLine} line
     *
     * @return {number|null}
     */
    getRequiredStockForLine(line) {
        let cartProductQty = this.getCartProductQuantityForLine(line);
        if (cartProductQty) {
            return cartProductQty.requiredQuantity;
        }
        return null;
    },

    /**
     * @param {CartLine} line
     *
     * @return {?StockLevel}
     */
    getStockLevelForLine(line) {
        return this.cartProductStockLevels.getStockLevel(
            line.branch.ID,
            line.item.ID
        ) || null;
    },

    /**
     * @param {CartLine} line
     *
     * @return {number|null}
     */
    getAvailableStockForLine(line) {
        const stockLevel = this.getStockLevelForLine(line);
        if (stockLevel) {
            return stockLevel.availableQty > 0 ? stockLevel.availableQty : 0;
        }
        return null;
    },

    /**
     * @param {CartLine} line
     *
     * @return {boolean}
     */
    isStockValidationDisabledForLine(line) {
        const stockLevel = this.getStockLevelForLine(line);
        if (stockLevel) {
            return stockLevel.disableStockValidation;
        }
        return false;
    },

    get hasOffersSelected() {
        return Object.keys(this.selectedManualOfferIDs).length > 0;
    },

    get hasCustomerData() {
        return this.customer && this.customer.hasData();
    },

    /**
     * Returns true if there are any applicable order level fields or item level fields which are not
     * marked as "ask early", regardless of if those fields have values entered.
     *
     * @returns {boolean}
     */
    get hasNonAskEarlyFields() {
        if (this.fields && this.fields.getNonAskEarlyInstances().length > 0) {
            // There are order level fields with values.
            return true;
        }

        for (let i = 0; i < this.itemList.length; i++) {
            for (let j = 0; j < this.itemList[i].itemInstances.length; j++) {
                if (this.itemList[i].itemInstances[j].fields
                    && this.itemList[i].itemInstances[j].fields.getNonAskEarlyInstances().length > 0) {
                    // There are line level fields.
                    return true;
                }
            }
        }

        return false;
    },

    /**
     * Returns true if some data has been entered for any of the order level fields or item level fields which are
     * not marked as "ask early".
     *
     * @returns {boolean}
     */
    get hasNonAskEarlyFieldData() {
        if (this.fields && this.fields.getNonAskEarlyInstancesWithValue().length > 0) {
            // There are order level fields with values.
            return true;
        }

        for (let i = 0; i < this.itemList.length; i++) {
            for (let j = 0; j < this.itemList[i].itemInstances.length; j++) {
                if (this.itemList[i].itemInstances[j].fields
                    && this.itemList[i].itemInstances[j].fields.getNonAskEarlyInstancesWithValue().length > 0) {
                    // There are line level fields with values.
                    return true;
                }
            }
        }

        return false;
    },

    /**
     * @param {number|null} orderID
     */
    setUnconfirmedOrderID(orderID) {
        // If we are changing the unconfirmed order ID (including setting it to null), store the previous one.
        if (this.unconfirmedOrderID && orderID !== this.unconfirmedOrderID) {
            this.previousUnconfirmedOrderIDs.push(this.unconfirmedOrderID);
        }

        this.unconfirmedOrderID = orderID;
    },

    /**
     * @returns {number}
     */
    get ordersAttempted() {
        return this.previousUnconfirmedOrderIDs.length;
    },

    /**
     * @returns {number[]}
     */
    getErrorIDs() {
        const orderSaveErrorIDs = this.orderSaveErrors.map((error) => error.errorID).filter((id) => id);
        const paymentSaveErrorIDs = this.paymentSaveErrors.map((error) => error.errorID).filter((id) => id);
        return [...new Set([...orderSaveErrorIDs, ...paymentSaveErrorIDs])];
    },
};

module.exports = Cart;
